import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
  useTheme,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
} from "@mui/material";
import {
  Phone,
  Business,
  Person,
  AttachMoney,
  CalendarToday,
  Comment,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Category,
} from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";
import { IStoreState } from "../../../redux/initialStoreState";
import { RightPanel } from "../../../components/RightPanel";
import { fetchHistoryByTableColumnAsync } from "../../../redux/common/commonActions";
import { useDispatchWrapper } from "../../../hooks";
import { CustomChip } from "../../../components/CustomChip";
import { HistoryRow } from "./HistoryRow";
import { groupHistoryRecords } from "../../../helpers/historyHelpers";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";
import { LeadStatusRenderer } from "../../../components/LeadStatusRenderer/LeadStatusRenderer";
import { EventChip } from "../../../components/ChipsRenderer/EventChip";
import { LoadState, MODULE_NAMES } from "../../../constants/enums";
import { fetchLeadAsync } from "../../../redux/leads/leadsActions";
import { fetchActionItemAsync } from "../../../redux/actionItems/actionItem.action";
import { useBillingCompanyContext } from "../../../providers";
import { ILead } from "../../../redux/leads/leads.types";

interface IViewHistoryDetailsProps {
  open: boolean;
  onClose: () => void;
  moduleInformation: any;
  initialLeadDetails?: any;
}

export const ViewHistoryDetailsRightPanel: React.FC<
  IViewHistoryDetailsProps
> = (props) => {
  const { open, onClose, moduleInformation, initialLeadDetails } = props;
  const theme = useTheme();
  const dispatch = useDispatchWrapper();
  const { ftDate } = useTimezoneFormatter();
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const companyOutlet = useBillingCompanyContext();
  const companyOutletUuid = companyOutlet?.comapny?.company_outlet_uuid;

  const { list, data, loading: historyLoading } = useSelector(
    (storeState: IStoreState) => storeState.common.generalHistory,
  );

  const requestedLead = useSelector(
    (storeState: IStoreState) => storeState.leads.lead.data,
  );
  const requestedActionItem = useSelector(
    (storeState: IStoreState) => storeState.actionItems?.actionItem?.data,
  );
  const leadsList = useSelector(
    (storeState: IStoreState) => storeState.leads.list.list,
  );
  const actionItemsList = useSelector(
    (storeState: IStoreState) => storeState.actionItems?.list?.list,
  );

  useEffect(() => {
    if (open && moduleInformation?.moduleId) {
      dispatch(fetchHistoryByTableColumnAsync(moduleInformation.moduleId));
      if (moduleInformation?.moduleName === MODULE_NAMES.CHAT_LEAD) {
        dispatch(fetchActionItemAsync(moduleInformation.moduleId));
      } else if (companyOutletUuid) {
        dispatch(
          fetchLeadAsync({
            leadId: moduleInformation.moduleId,
            company_outlet_uuid: companyOutletUuid,
            skipLoader: true,
          })
        );
      }
    }
  }, [open, moduleInformation?.moduleId, moduleInformation?.moduleName, companyOutletUuid, dispatch]);

  const historyList = data?.result || list || [];
  const groupedHistory = React.useMemo(
    () => groupHistoryRecords(historyList),
    [historyList],
  );

  // Count distinct leads (by leads_uuid), not raw history rows.
  const distinctLeadCount = React.useMemo(() => {
    const uuids = new Set<string>();
    (historyList || []).forEach((item: any) => {
      const uuid =
        item?.lead?.leads_uuid ||
        item?.module_uuid ||
        item?.payload?.identifier?.column_value;
      if (uuid) uuids.add(uuid);
    });
    return uuids.size || historyList.length;
  }, [historyList]);
  const isCustomerOverview = Boolean(
    moduleInformation?.customerId || moduleInformation?.fromCustomerOverview,
  );

  const leadData = React.useMemo(() => {
    if (initialLeadDetails) {
      return initialLeadDetails;
    }
    if (!isCustomerOverview) {
      if (moduleInformation?.moduleName === MODULE_NAMES.CHAT_LEAD) {
        if (requestedActionItem && requestedActionItem.action_item_uuid === moduleInformation?.moduleId) {
          return requestedActionItem;
        }
        if (actionItemsList && actionItemsList.length > 0 && moduleInformation?.moduleId) {
          const found = actionItemsList.find((item) => item.action_item_uuid === moduleInformation.moduleId);
          if (found) return found;
        }
      } else {
        if (requestedLead && requestedLead.leads_uuid === moduleInformation?.moduleId) {
          return requestedLead;
        }
        if (leadsList && leadsList.length > 0 && moduleInformation?.moduleId) {
          const found = leadsList.find((l) => l.leads_uuid === moduleInformation.moduleId);
          if (found) return found;
        }
        if (actionItemsList && actionItemsList.length > 0 && moduleInformation?.moduleId) {
          const found = actionItemsList.find((item) => item.action_item_uuid === moduleInformation.moduleId);
          if (found) return found;
        }
      }
    }

    if (data && Object.keys(data).length > 0) {
      if (
        data.name_of_guest ||
        data.customer_name ||
        data.contact_number_one ||
        data.name ||
        data.mobile_no
      ) {
        return data;
      }
    }
    if (historyList && historyList.length > 0) {
      const firstItem = historyList[0];
      if (firstItem?.payload?.no_changed_column) {
        return firstItem.payload.no_changed_column;
      }
      if (firstItem?.payload) {
        return firstItem.payload;
      }
    }

    return data || {};
  }, [
    data,
    historyList,
    requestedLead,
    leadsList,
    actionItemsList,
    moduleInformation?.moduleId,
    isCustomerOverview,
  ]);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <RightPanel
      open={open}
      heading={isCustomerOverview ? "Customer History" : "Lead History"}
      onClose={onClose}
      width="50%"
      disablePadding
      actionButtons={() => (
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Button variant="contained" type="button" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      )}
    >
      <Box
        sx={{
          background: "#eef2f7",
          minHeight: "100%",
          pr: 1
        }}
      >
        <Grid container spacing={{ xs: 0, md: 1 }}>
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                background: "#fff",
                borderRadius: 2,
                boxShadow: "none",
                height: { xs: "auto", md: "fit-content" },
                maxHeight: { md: "calc(100vh - 160px)" },
                overflowY: { md: "auto" },
                position: { md: "sticky" },
                top: { md: 20 },
                border: 0,
              }}
            >
              <CardContent sx={{ padding: 0 }} >
                {historyLoading === LoadState.InProgress || historyLoading === LoadState.NotLoaded ? (
                  <LeftPanelSkeleton />
                ) : (
                <>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                 <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {leadData?.name_of_guest ||
                      leadData?.customer_name ||
                      leadData?.first_name ||
                      leadData?.name ||
                      "Lead"}
                  </Typography>
                  {!isCustomerOverview && leadData?.status && (
                    <LeadStatusRenderer
                      status={leadData.status}
                      actualStatus={leadData.actual_status || leadData.status}
                    />
                  )}
                </Stack>
               
                <Stack spacing={2}>
                  {!isCustomerOverview && leadData?.event_type && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                      <EventChip eventType={leadData.event_type} otherEventName={leadData.other_event_name || ""} />
                    </Stack>
                  )}
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, my: 1 }}
                    >
                      Lead Attributes
                    </Typography>
                    {isMobile ? (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                          onClick={() =>
                            setShowLeadDetails((prev: boolean) => !prev)
                          }
                        >
                          <Typography variant="subtitle2" fontWeight={600}>
                            Show Details
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowLeadDetails((prev: boolean) => !prev);
                            }}
                          >
                            {showLeadDetails ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                        <Collapse in={showLeadDetails} timeout="auto">
                          <Stack
                            spacing={{ xs: 0.5, md: 0.75 }}
                          >
                            <DetailRow
                              icon={Phone}
                              label="Phone"
                              value={
                                leadData?.contact_number_one ||
                                leadData?.contact_number ||
                                leadData?.mobile_no ||
                                "--"
                              }
                            />
                            {!isCustomerOverview && leadData?.preferred_venue && (
                              <DetailRow
                                icon={Business}
                                label="Preferred Venue"
                                value={leadData.preferred_venue}
                              />
                            )}
                            {!isCustomerOverview && leadData?.event_type && (
                              <DetailRow
                                icon={Category}
                                label="Event Type"
                                value={leadData.event_type}
                              />
                            )}

                            {!isCustomerOverview &&
                              leadData?.number_of_guests !== undefined && (
                                <DetailRow
                                  icon={AttachMoney}
                                  label="Number of Guests"
                                  value={`${leadData.number_of_guests}`}
                                />
                              )}
                            {!isCustomerOverview &&
                              leadData?.number_of_rooms !== undefined && (
                                <DetailRow
                                  icon={Business}
                                  label="Number of Rooms"
                                  value={`${leadData.number_of_rooms}`}
                                />
                              )}
                            {!isCustomerOverview && leadData?.function_date && (
                              <DetailRow
                                icon={CalendarToday}
                                label="Event Date"
                                value={ftDate(leadData.function_date)}
                              />
                            )}
                            {!isCustomerOverview && leadData?.function_month && (
                              <DetailRow
                                icon={CalendarToday}
                                label="Function Month"
                                value={leadData.function_month}
                              />
                            )}
                            {!isCustomerOverview && leadData?.date_of_visit && (
                              <DetailRow
                                icon={CalendarToday}
                                label="Last Visit"
                                value={ftDate(leadData.date_of_visit)}
                              />
                            )}
                            {!isCustomerOverview && leadData?.followup_date && (
                              <DetailRow
                                icon={CalendarToday}
                                label="Follow-up"
                                value={ftDate(leadData.followup_date)}
                              />
                            )}
                            {!isCustomerOverview && (leadData?.remarks || leadData?.description) && (
                              <DetailRow
                                icon={Comment}
                                label="Remarks"
                                value={leadData.remarks || leadData.description}
                              />
                            )}
                            {!isCustomerOverview &&
                              leadData?.handled_by_employee && (
                                <DetailRow
                                  icon={Person}
                                  label="Handled By"
                                  value={leadData.handled_by_employee}
                                />
                              )}
                          </Stack>
                        </Collapse>
                      </>
                    ) : (
                      <Stack
                        spacing={{ xs: 0.5, md: 1.5 }}
                      >
                        <DetailRow
                          icon={Phone}
                          label="Phone"
                          value={
                            leadData?.contact_number_one ||
                            leadData?.contact_number ||
                            leadData?.mobile_no ||
                            "--"
                          }
                        />
                        {!isCustomerOverview && leadData?.preferred_venue && (
                          <DetailRow
                            icon={Business}
                            label="Preferred Venue"
                            value={leadData.preferred_venue}
                          />
                        )}
                        {!isCustomerOverview && leadData?.event_type && (
                          <DetailRow
                            icon={Category}
                            label="Event Type"
                            value={leadData.event_type}
                          />
                        )}

                        {!isCustomerOverview &&
                          leadData?.number_of_guests !== undefined && (
                            <DetailRow
                              icon={AttachMoney}
                              label="Number of Guests"
                              value={`${leadData.number_of_guests}`}
                            />
                          )}
                        {!isCustomerOverview &&
                          leadData?.number_of_rooms !== undefined && (
                            <DetailRow
                              icon={Business}
                              label="Number of Rooms"
                              value={`${leadData.number_of_rooms}`}
                            />
                          )}
                        {!isCustomerOverview && leadData?.function_date && (
                          <DetailRow
                            icon={CalendarToday}
                            label="Event Date"
                            value={ftDate(leadData.function_date)}
                          />
                        )}
                        {!isCustomerOverview && leadData?.function_month && (
                          <DetailRow
                            icon={CalendarToday}
                            label="Function Month"
                            value={leadData.function_month}
                          />
                        )}
                        {!isCustomerOverview && leadData?.date_of_visit && (
                          <DetailRow
                            icon={CalendarToday}
                            label="Last Visit"
                            value={ftDate(leadData.date_of_visit)}
                          />
                        )}
                        {!isCustomerOverview && leadData?.followup_date && (
                          <DetailRow
                            icon={CalendarToday}
                            label="Follow-up"
                            value={ftDate(leadData.followup_date)}
                          />
                        )}
                        {!isCustomerOverview && (leadData?.remarks || leadData?.description) && (
                          <DetailRow
                            icon={Comment}
                            label="Remarks"
                            value={leadData.remarks || leadData.description}
                          />
                        )}
                        {!isCustomerOverview && leadData?.handled_by_employee && (
                          <DetailRow
                            icon={Person}
                            label="Handled By"
                            value={leadData.handled_by_employee}
                          />
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
                </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: { xs: "block", md: "none" },
            }}
          >
            <Divider sx={{ my: 2 }} />
          </Grid>
          <Grid item xs={12} md={7}>
            {isMobile ? (
              <Box sx={{ px: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#1a2a3a">
                      Activity history
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Every status change, threaded on the record spine.
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      <Typography variant="body2" component="span">
                        <strong style={{ color: "#1976d2" }}>{distinctLeadCount}</strong> leads
                      </Typography>
                    }
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500, borderRadius: "20px", px: 1, borderColor: "#e0e0e0" }}
                  />
                </Stack>

                <Stack spacing={1}>
                  {historyLoading === LoadState.InProgress || historyLoading === LoadState.NotLoaded ? (
                    <TimelineSkeleton />
                  ) : (
                    groupedHistory.map((group, index) => (
                      <HistoryRow
                        key={group.groupId}
                        group={group}
                        isLast={index === groupedHistory.length - 1}
                      />
                    ))
                  )}
                </Stack>
              </Box>
            ) : (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color="#1a2a3a">
                      Activity history
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Every status change, threaded on the record spine.
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      <Typography variant="body2" component="span">
                        <strong style={{ color: "#1976d2" }}>{distinctLeadCount}</strong> leads
                      </Typography>
                    }
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500, borderRadius: "20px", px: 1, borderColor: "#e0e0e0" }}
                  />
                </Stack>

                <Stack spacing={1}>
                  {historyLoading === LoadState.InProgress || historyLoading === LoadState.NotLoaded ? (
                    <TimelineSkeleton />
                  ) : (
                    groupedHistory.map((group, index) => (
                      <HistoryRow
                        key={group.groupId}
                        group={group}
                        isLast={index === groupedHistory.length - 1}
                      />
                    ))
                  )}
                </Stack>
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    </RightPanel>
  );
};

interface DetailRowProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon: Icon, label, value }) => {
  const displayValue = (value === null || value === undefined || value === "" || value === "null" || value === "undefined") ? "--" : value;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Typography variant="body2" fontSize={16} color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          mx: 1.5,
          borderBottom: "2px dotted #ccc",
          minWidth: 20,
          height: "2px",
          alignSelf: "center",
          mt: "2px",
        }}
      />
      <Typography variant="body2" fontSize={16} fontWeight={600} color="text.primary" sx={{ textAlign: "right" }}>
        {displayValue}
      </Typography>
    </Stack>
  );
};

const TimelineSkeleton: React.FC = () => {
  return (
    <Stack spacing={3} sx={{ width: "100%", py: 1 }}>
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ display: "flex", gap: 1.5 }}>
          {/* Avatar dot */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 36 }}>
            <Skeleton variant="circular" width={36} height={36} />
            {item < 3 && (
              <Skeleton variant="rectangular" width={2} height={60} sx={{ mt: 1 }} />
            )}
          </Box>
          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: "12px" }} />
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: "12px" }} />
            </Stack>
            <Skeleton variant="rounded" width="100%" height={70} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const LeftPanelSkeleton: React.FC = () => {
  return (
    <Box sx={{ p: 2 }}>
      {/* Name + status */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Skeleton variant="text" width="60%" height={36} />
        <Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: "12px" }} />
      </Stack>
      {/* Event chip */}
      <Skeleton variant="rounded" width={90} height={26} sx={{ borderRadius: "16px", mb: 3 }} />
      {/* Lead Attributes heading */}
      <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
      {/* Detail rows */}
      <Stack spacing={1.5}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
          <Stack key={item} direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="text" width="30%" height={20} />
            <Box sx={{ flex: 1, mx: 1.5, borderBottom: "2px dotted #eee" }} />
            <Skeleton variant="text" width="35%" height={20} />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
